/**
 * 安全的 i18n 语言检测插件 (客户端)
 * 与服务端保持一致，确保 SSR/CSR 语言同步
 * @author NUXT 技术专家
 * @created 2024/12/19
 */

import { getLanguageByHost } from '../utils/language-detection'

export default defineNuxtPlugin({
  name: 'i18n-safe-detection-client',
  dependsOn: ['i18n:plugin'],
  async setup(nuxtApp) {
    // 只在客户端运行
    if (process.server) return

    // 🔒 防止重复执行检查
    if (nuxtApp._i18nSafeDetectionClientExecuted) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Safe i18n Detection Client] 插件已执行，跳过重复执行')
      }
      return
    }
    nuxtApp._i18nSafeDetectionClientExecuted = true

    // 🔒 客户端安全获取 host 信息
    const originalHost = window.location.host
    const host = originalHost.split(':')[0]

    // 基于 host 检测语言
    const detectedLanguage = getLanguageByHost(host)
    const locale = detectedLanguage === 'en' ? 'en' : 'zh'

    // 🔍 详细的调试信息 (总是输出，便于诊断问题)
    console.log(`[Safe i18n Detection Client] 🔒 客户端语言检测:`)
    console.log(`  📍 客户端Host: ${host}`)
    console.log(`  📍 检测语言: ${detectedLanguage}`)
    console.log(`  📍 目标Locale: ${locale}`)

    // 🔒 检查服务端传递的语言信息是否一致
    const serverDetectedLanguage = nuxtApp.$detectedLanguage
    const serverDetectedLocale = nuxtApp.$detectedLocale

    if (serverDetectedLanguage && serverDetectedLanguage !== detectedLanguage) {
      console.warn(
        `[Safe i18n Detection Client] ⚠️ 客户端语言检测与服务端不一致: 客户端=${detectedLanguage}, 服务端=${serverDetectedLanguage}`
      )
    }

    // 🚀 性能优化：只在检测到英文时才进行语言切换
    // 使用服务端检测结果优先，避免不必要的开销
    const finalDetectedLanguage = serverDetectedLanguage || detectedLanguage
    const finalDetectedLocale = serverDetectedLocale || locale

    if (finalDetectedLanguage === 'cn') {
      //console.log(`[Safe i18n Detection Client] ⚡ 优化：检测到中文站点，跳过客户端语言切换 (默认配置)`)

      // 🔒 仍然提供语言信息给组件使用，但跳过 i18n 切换
      if (!nuxtApp.$detectedLanguage) {
        nuxtApp.provide('detectedLanguage', finalDetectedLanguage)
      }
      if (!nuxtApp.$detectedLocale) {
        nuxtApp.provide('detectedLocale', finalDetectedLocale)
      }
      if (!nuxtApp.$originalHost) {
        nuxtApp.provide('originalHost', originalHost)
      }
      return
    }

    //console.log(`[Safe i18n Detection Client] 🌐 检测到英文站点，执行客户端语言切换: ${finalDetectedLanguage}`)

    // 🔒 客户端安全设置语言
    nuxtApp.runWithContext(() => {
      const { $i18n } = nuxtApp

      if ($i18n && typeof $i18n === 'object') {
        const i18nInstance = $i18n as any

        // 🔍 客户端 i18n 状态检查
        console.log(`[Safe i18n Detection Client] 🔍 客户端 i18n 状态:`)
        console.log(`  📍 服务端语言: ${serverDetectedLanguage}`)
        console.log(`  📍 服务端Locale: ${serverDetectedLocale}`)
        console.log(`  📍 当前 locale.value: ${i18nInstance.locale?.value || 'N/A'}`)

        if (i18nInstance.locale && typeof i18nInstance.locale === 'object' && 'value' in i18nInstance.locale) {
          // 检查当前locale是否需要调整
          const currentLocale = i18nInstance.locale.value
          const targetLocale = finalDetectedLocale // 使用优化后的最终检测结果

          console.log(`  📍 目标 locale: ${targetLocale} (来源: ${serverDetectedLocale ? '服务端' : '客户端检测'})`)

          if (currentLocale !== targetLocale) {
            // 🔑 关键修复：使用 setLocale 而不是直接设置 locale.value
            // 这样能确保翻译文件被正确加载
            if (i18nInstance.setLocale && typeof i18nInstance.setLocale === 'function') {
              try {
                i18nInstance
                  .setLocale(targetLocale)
                  .then(() => {
                    //console.log(`[Safe i18n Detection Client] ✅ 客户端语言已切换 (setLocale): ${currentLocale} -> ${targetLocale}`)
                  })
                  .catch((error: any) => {
                    console.error(`[Safe i18n Detection Client] ❌ setLocale 失败:`, error)
                    // 回退到直接设置
                    i18nInstance.locale.value = targetLocale
                    console.log(`[Safe i18n Detection Client] ✅ 回退设置成功: ${targetLocale}`)
                  })
              } catch (error) {
                console.error(`[Safe i18n Detection Client] ❌ setLocale 调用失败:`, error)
                // 回退到直接设置
                i18nInstance.locale.value = targetLocale
                console.log(
                  `[Safe i18n Detection Client] ✅ 客户端语言已切换 (fallback): ${currentLocale} -> ${targetLocale}`
                )
              }
            } else {
              // 回退到直接设置
              i18nInstance.locale.value = targetLocale
              console.log(
                `[Safe i18n Detection Client] ✅ 客户端语言已切换 (fallback): ${currentLocale} -> ${targetLocale}`
              )
            }
          } else {
            console.log(`[Safe i18n Detection Client] ✅ 客户端语言已正确: ${targetLocale}`)
          }
        } else {
          console.warn(`[Safe i18n Detection Client] ⚠️ 无法设置客户端语言，i18n 结构不符合预期:`, {
            hasLocale: !!i18nInstance.locale,
            localeType: typeof i18nInstance.locale,
            hasValue: i18nInstance.locale && 'value' in i18nInstance.locale
          })
        }
      } else {
        console.warn(`[Safe i18n Detection Client] ⚠️ 客户端 i18n 实例不可用`)
      }
    })

    // 🔒 提供客户端语言信息 - 避免重复定义
    if (!nuxtApp.$detectedLanguage) {
      nuxtApp.provide('detectedLanguage', finalDetectedLanguage)
    }
    if (!nuxtApp.$detectedLocale) {
      nuxtApp.provide('detectedLocale', finalDetectedLocale)
    }
    if (!nuxtApp.$originalHost) {
      nuxtApp.provide('originalHost', originalHost)
    }
  }
})
